import { DatePicker, FormField, ListToolbar, Select, type ListToolbarProps } from '@ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { formatDate } from '../../../lib/format';
import { useDatePresets, useFacetTexts, useRangeLabel } from '../../../lib/facets';
import { useSession } from '../../identity/session';
import { useColonies, useLocations, type RecordFilter } from '../api';

const PARAMS = { colony_id: 'volk', location_id: 'standort', from: 'von', to: 'bis' } as const;

/** Filter of the record lists, kept in the URL (docs/ux-richtlinien.md §6.3). */
export function useRecordFilter(): [
  RecordFilter,
  (patch: Partial<RecordFilter>) => void,
  () => void,
] {
  const [params, setParams] = useSearchParams();
  const filter: RecordFilter = {
    ...(params.get('volk') ? { colony_id: params.get('volk') ?? undefined } : {}),
    ...(params.get('standort') ? { location_id: params.get('standort') ?? undefined } : {}),
    ...(params.get('von') ? { from: params.get('von') ?? undefined } : {}),
    ...(params.get('bis') ? { to: params.get('bis') ?? undefined } : {}),
    ...(params.get('laufend') ? { running: true } : {}),
  };
  const set = useCallback(
    (patch: Partial<RecordFilter>) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            const name = key === 'running' ? 'laufend' : PARAMS[key as keyof typeof PARAMS];
            if (!name) continue;
            if (value === undefined || value === '' || value === false) next.delete(name);
            else next.set(name, value === true ? '1' : String(value));
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const reset = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);
  return [filter, set, reset];
}

export interface RecordToolbarProps {
  value: RecordFilter;
  onChange: (patch: Partial<RecordFilter>) => void;
  onReset: () => void;
  /** Offers "Alle · Laufend" (treatments). */
  running?: boolean;
  /** The phones' sort menu (§6.4). */
  sort?: ListToolbarProps['sort'];
}

/** Toolbar of the record lists: scope, filters in a popover, chips (TRT-004, FED-005). */
export function RecordToolbar({
  value,
  onChange,
  onReset,
  running = false,
  sort,
}: RecordToolbarProps) {
  const { t } = useTranslation();
  const { can } = useSession();
  const colonies = useColonies({ status: 'all' }, can('colonies.read'));
  const locations = useLocations(true, can('locations.read'));
  const colonyName = colonies.data?.find((c) => c.id === value.colony_id)?.name;
  const locationName = locations.data?.find((l) => l.id === value.location_id)?.name;
  const chips = [
    ...(value.colony_id
      ? [
          {
            key: 'colony',
            label: t('apiary.filters.chipColony', { name: colonyName ?? '…' }),
            onRemove: () => onChange({ colony_id: undefined }),
          },
        ]
      : []),
    ...(value.location_id
      ? [
          {
            key: 'location',
            label: t('apiary.filters.chipLocation', { name: locationName ?? '…' }),
            onRemove: () => onChange({ location_id: undefined }),
          },
        ]
      : []),
    ...(value.from
      ? [
          {
            key: 'from',
            label: t('apiary.filters.chipFrom', { date: formatDate(value.from) }),
            onRemove: () => onChange({ from: undefined }),
          },
        ]
      : []),
    ...(value.to
      ? [
          {
            key: 'to',
            label: t('apiary.filters.chipTo', { date: formatDate(value.to) }),
            onRemove: () => onChange({ to: undefined }),
          },
        ]
      : []),
  ];
  const facetTexts = useFacetTexts(onReset);
  const presets = useDatePresets();
  const range = useRangeLabel(value.from ?? '', value.to ?? '');
  const props: ListToolbarProps = {
    facets: {
      ...facetTexts,
      items: [
        ...(can('colonies.read')
          ? [
              {
                type: 'select' as const,
                key: 'colony',
                label: t('apiary.filters.colony'),
                value: value.colony_id ?? '',
                options: (colonies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                onChange: (v: string) => onChange({ colony_id: v || undefined }),
              },
            ]
          : []),
        ...(can('locations.read')
          ? [
              {
                type: 'select' as const,
                key: 'location',
                label: t('apiary.filters.location'),
                value: value.location_id ?? '',
                options: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                onChange: (v: string) => onChange({ location_id: v || undefined }),
              },
            ]
          : []),
        {
          type: 'dates' as const,
          key: 'period',
          label: t('apiary.filters.period'),
          from: value.from ?? '',
          to: value.to ?? '',
          fromLabel: t('apiary.filters.from'),
          toLabel: t('apiary.filters.to'),
          valueLabel: range,
          presets,
          onChange: (r: { from: string; to: string }) =>
            onChange({ from: r.from || undefined, to: r.to || undefined }),
        },
      ],
    },
    filters: {
      label: t('common.filter'),
      activeCount: chips.length,
      content: (
        <>
          {can('colonies.read') && (
            <FormField label={t('apiary.filters.colony')}>
              {(p) => (
                <Select
                  {...p}
                  value={value.colony_id ?? ''}
                  onChange={(e) => onChange({ colony_id: e.target.value || undefined })}
                  options={[
                    { value: '', label: t('apiary.filters.all') },
                    ...(colonies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
              )}
            </FormField>
          )}
          {can('locations.read') && (
            <FormField label={t('apiary.filters.location')}>
              {(p) => (
                <Select
                  {...p}
                  value={value.location_id ?? ''}
                  onChange={(e) => onChange({ location_id: e.target.value || undefined })}
                  options={[
                    { value: '', label: t('apiary.filters.all') },
                    ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                  ]}
                />
              )}
            </FormField>
          )}
          <FormField label={t('apiary.filters.from')}>
            {(p) => (
              <DatePicker
                {...p}
                value={value.from ?? ''}
                onChange={(v) => onChange({ from: v || undefined })}
              />
            )}
          </FormField>
          <FormField label={t('apiary.filters.to')}>
            {(p) => (
              <DatePicker
                {...p}
                value={value.to ?? ''}
                onChange={(v) => onChange({ to: v || undefined })}
              />
            )}
          </FormField>
        </>
      ),
    },
    chips: {
      chips,
      removeLabel: (label) => t('common.removeFilter', { label }),
      resetLabel: t('common.resetAll'),
      onReset,
    },
    ...(running
      ? {
          scope: {
            label: t('apiary.filters.scope'),
            value: value.running ? 'running' : 'all',
            onChange: (v: string) => onChange({ running: v === 'running' || undefined }),
            options: [
              { value: 'all', label: t('apiary.filters.scopeAll') },
              { value: 'running', label: t('apiary.filters.scopeRunning') },
            ],
          },
        }
      : {}),
  };
  return <ListToolbar {...props} {...(sort ? { sort } : {})} />;
}

/** Whether any record filter is set. */
export function isFiltered(filter: RecordFilter): boolean {
  return Object.values(filter).some((v) => v !== undefined && v !== '' && v !== false);
}
